'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import Icon from '@/components/ui/AppIcon';


const openings = [
  {
    title: 'Senior Full-Stack Engineer',
    team: 'Engineering',
    location: 'Bangalore (Hybrid)',
    type: 'Full-time',
    icon: 'CodeBracketIcon',
    color: '#1A237E',
    bg: '#EEF0FB',
    description: 'Build and scale the core platform that powers thousands of product reviews daily. Work with Next.js, Supabase, and modern tooling.',
    skills: ['Next.js', 'TypeScript', 'PostgreSQL', 'Supabase'],
  },
  {
    title: 'Product Manager – Reviewer Experience',
    team: 'Product',
    location: 'Bangalore (Hybrid)',
    type: 'Full-time',
    icon: 'RectangleGroupIcon',
    color: '#FF5722',
    bg: '#FFF3EE',
    description: 'Own the end-to-end reviewer journey — from onboarding to payout. Define roadmap, run experiments, and drive engagement metrics.',
    skills: ['Product Strategy', 'Data Analysis', 'User Research', 'Agile'],
  },
  {
    title: 'Growth Marketing Manager',
    team: 'Marketing',
    location: 'Remote (India)',
    type: 'Full-time',
    icon: 'MegaphoneIcon',
    color: '#00BFA5',
    bg: '#E0F7F4',
    description: 'Drive reviewer and brand acquisition through performance marketing, SEO, and content. Own CAC and LTV metrics.',
    skills: ['Performance Marketing', 'SEO', 'Analytics', 'Content Strategy'],
  },
  {
    title: 'Brand Partnerships Executive',
    team: 'Sales',
    location: 'Mumbai / Bangalore',
    type: 'Full-time',
    icon: 'HandshakeIcon',
    color: '#7B1FA2',
    bg: '#F3E5F5',
    description: 'Onboard D2C brands and e-commerce sellers onto the platform. Build long-term relationships and drive campaign revenue.',
    skills: ['B2B Sales', 'CRM', 'Negotiation', 'E-commerce'],
  },
  {
    title: 'Data Analyst',
    team: 'Data',
    location: 'Bangalore (Hybrid)',
    type: 'Full-time',
    icon: 'ChartBarIcon',
    color: '#F59E0B',
    bg: '#FFFBEB',
    description: 'Analyse reviewer behaviour, campaign performance, and platform health. Build dashboards and surface insights that drive decisions.',
    skills: ['SQL', 'Python', 'Tableau / Metabase', 'Statistics'],
  },
  {
    title: 'Customer Success Associate',
    team: 'Operations',
    location: 'Remote (India)',
    type: 'Full-time',
    icon: 'HeartIcon',
    color: '#E91E63',
    bg: '#FCE4EC',
    description: 'Support reviewers and brands through onboarding, campaign issues, and payout queries. Be the voice of the customer internally.',
    skills: ['Communication', 'Problem Solving', 'CRM Tools', 'Hindi + English'],
  },
];

const perks = [
  { icon: 'BanknotesIcon', title: 'Competitive Salary', desc: 'Market-rate compensation + ESOPs for early team members' },
  { icon: 'HomeModernIcon', title: 'Flexible Work', desc: 'Hybrid and remote options — we care about output, not hours' },
  { icon: 'AcademicCapIcon', title: 'Learning Budget', desc: '₹30,000/year for courses, conferences, and books' },
  { icon: 'HeartIcon', title: 'Health Insurance', desc: 'Comprehensive health cover for you and your family' },
  { icon: 'SunIcon', title: 'Unlimited Leaves', desc: 'Take the time you need — we trust you to manage your work' },
  { icon: 'UserGroupIcon', title: 'Small Team, Big Impact', desc: 'Work directly with founders and see your work ship fast' },
];

export default function CareersPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0F0F1A] text-white py-20 px-4">
          <div className="absolute inset-0 pointer-events-none opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle, #FF5722 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          <div className="relative max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-widest text-white/70">We&apos;re Hiring</span>
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl mb-5 leading-tight">
              Build the future of<br />
              <span className="text-primary">honest reviews</span> in India
            </h1>
            <p className="text-white/50 text-base max-w-2xl leading-relaxed mb-8">
              Join a small, ambitious team solving a real problem — fake reviews are destroying trust in Indian e-commerce. We&apos;re building the platform that fixes it.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2 text-white/60">
                <Icon name="MapPinIcon" size={15} />
                Bangalore, India (+ Remote)
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Icon name="UsersIcon" size={15} />
                15–30 person team
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Icon name="RocketLaunchIcon" size={15} />
                Series A stage
              </div>
            </div>
          </div>
        </section>

        {/* Perks */}
        <section className="bg-[#F7F8FA] py-14 px-4 border-b border-gray-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-2xl text-gray-900 mb-8 text-center">Why join us?</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {perks.map((p) => (
                <div key={p.title} className="bg-white rounded-2xl p-5 border border-gray-100">
                  <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center mb-3">
                    <Icon name={p.icon as any} size={18} className="text-primary" />
                  </div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">{p.title}</p>
                  <p className="text-xs text-gray-400 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Roles */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl text-gray-900 mb-2">Open Positions</h2>
          <p className="text-gray-500 text-sm mb-8">{openings.length} roles across Engineering, Product, Marketing, and more</p>

          <div className="space-y-4">
            {openings.map((job) => (
              <div key={job.title} className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-md transition-all group">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: job.bg }}>
                    <Icon name={job.icon as any} size={20} style={{ color: job.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 text-base group-hover:text-primary transition-colors">{job.title}</h3>
                      <span className="text-xs font-medium bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full">{job.team}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-400 mb-3">
                      <span className="flex items-center gap-1"><Icon name="MapPinIcon" size={12} />{job.location}</span>
                      <span className="flex items-center gap-1"><Icon name="BriefcaseIcon" size={12} />{job.type}</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3 leading-relaxed">{job.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((s) => (
                        <span key={s} className="text-xs bg-orange-50 text-orange-600 border border-orange-100 px-2.5 py-1 rounded-full font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                  <a
                    href={`mailto:careers@indianreviewclub.com?subject=Application: ${job.title}`}
                    className="shrink-0 inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-primary/90 transition-colors"
                  >
                    Apply
                    <Icon name="ArrowRightIcon" size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* No role? */}
        <section className="bg-[#F7F8FA] border-t border-gray-100 py-14 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl text-gray-900 mb-3">Don&apos;t see your role?</h2>
            <p className="text-gray-500 text-sm mb-6">
              We&apos;re always looking for exceptional people. Send us your resume and tell us how you&apos;d contribute.
            </p>
            <a
              href="mailto:careers@indianreviewclub.com?subject=Open Application"
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors text-sm"
            >
              <Icon name="EnvelopeIcon" size={16} />
              Send Open Application
            </a>
          </div>
        </section>
      </main>
      <Footer />
      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
